// src/lib/api_whatsapp_signup.ts
// Cliente de Orión para conectar WhatsApp por Embedded Signup de Meta con
// COEXISTENCIA (el número sigue en la app WhatsApp Business del teléfono).
// Backend: Orion-Brainstream api/routes/whatsapp_signup.py
//   → services/whatsapp_embedded_signup.py (single-source).
// El org_id NO se manda: el backend lo toma del JWT (multi-tenant estricto).

import { apiUrl } from '@/config';
import { ensureFreshToken } from '@/lib/tokenRefresh';

export class WaSignupError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'WaSignupError';
    this.status = status;
  }
}

async function orionFetch<T>(path: string, init?: RequestInit): Promise<T> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new WaSignupError('Sesión expirada. Inicia sesión nuevamente.', 401);
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail : `Error ${res.status}`;
    throw new WaSignupError(detail, res.status);
  }
  return data as T;
}

export interface WaSignupConfig {
  enabled: boolean;
  app_id: string | null;
  graph_version: string | null;
  sync_disponible: boolean;
  motivo?: string;
}

export interface WaNumero {
  phone_number_id: string;
  waba_id: string | null;
  display_name: string | null;
  is_default: boolean;
  enabled: boolean;
  via_embedded_signup: boolean;
  display_phone_number?: string | null;
  verified_name?: string | null;
  platform_type?: string | null;
}

export interface WaSignupStatus {
  org_id: string;
  conectado: boolean;
  numeros: WaNumero[];
}

export interface WaSignupResult {
  ok: boolean;
  creado: boolean;
  numero: WaNumero;
  coexistencia: boolean;
  otras_apps_suscritas: string[];
  sincronizacion: Record<string, string | boolean> | null;
  aviso_sincronizacion: string | null;
  nota: string;
}

const BASE = '/admin/whatsapp/embedded-signup';

export const getWaSignupConfig = () => orionFetch<WaSignupConfig>(`${BASE}/config`);
export const getWaSignupStatus = () => orionFetch<WaSignupStatus>(`${BASE}/status`);
export const completeWaSignup = (body: {
  code: string;
  waba_id: string;
  phone_number_id: string;
  make_default?: boolean;
  sincronizar_historial?: boolean;
}) => orionFetch<WaSignupResult>(`${BASE}/complete`, { method: 'POST', body: JSON.stringify(body) });
